import { useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Badge, Modal, ModalFooter, ContentLoader, BackButton } from '@/components/common';
import { useUser, useDeleteUser } from '@/hooks/useUsers';
import { userStatusOf } from '@/services/users';
import { USER_STATUS_BADGE_VARIANT, USER_STATUS_LABELS } from './useSetUserStatus';
import { UserAccountActions } from './UserAccountActions';
import { formatDateTime, titleCase } from '@/utils';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /**
   * The broker this page was opened FROM (`?broker=<id>`), exactly as
   * `UserFormPage` reads it. `/users` is routed for super_admin and
   * broker_admin only, so every hard-coded way out of this page -- Back, the
   * breadcrumb, "not found", after-delete -- dropped a country admin on Access
   * Denied. A broker id, not a return URL: it can only produce a link into
   * /brokers/<id>, so it cannot bounce anybody elsewhere.
   */
  const brokerContext = searchParams.get('broker');
  const brokerReturn = brokerContext
    ? `/brokers/${encodeURIComponent(brokerContext)}?tab=admin`
    : null;
  const exitTo = brokerReturn ?? '/users';
  const editHref = brokerContext
    ? `/users/${id}/edit?broker=${encodeURIComponent(brokerContext)}`
    : `/users/${id}/edit`;

  const { data: user, isLoading, error } = useUser(id!);
  const deleteMutation = useDeleteUser();

  const handleDelete = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => navigate(exitTo),
    });
  };

  const getRoleBadgeVariant = (role: string) => {
    if (role.includes('admin')) return 'error';
    if (role.includes('posp')) return 'warning';
    return 'info';
  };

  const currentStatus = user ? userStatusOf(user) : null;

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User not found</h2>
        <p className="text-gray-500 mt-2 dark:text-gray-400">The user you are looking for does not exist.</p>
        <Link to={exitTo} className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            {brokerReturn ? 'Back to Broker' : 'Back to Users'}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to={exitTo} />
      </div>
      <PageHeader
        title={user.full_name}
        subtitle={titleCase(user.role)}
        breadcrumbs={[
          brokerReturn
            ? { label: 'Broker', href: brokerReturn }
            : { label: 'Users', href: '/users' },
          { label: user.full_name },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <Link to={editHref}>
              <Button variant="outline" leftIcon={<PencilIcon className="w-5 h-5" />}>
                Edit
              </Button>
            </Link>
            <Button
              variant="danger"
              leftIcon={<TrashIcon className="w-5 h-5" />}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader title="Contact Information" />
            <div className="space-y-4">
              {user.phone && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <PhoneIcon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{user.phone}</p>
                  </div>
                </div>
              )}
              {user.email && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <EnvelopeIcon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Role & Permissions */}
          <Card>
            <CardHeader title="Role & Access" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <Badge variant={getRoleBadgeVariant(user.role)} className="mt-1">
                  {titleCase(user.role)}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tenant Type</p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {user.tenant_type ? titleCase(user.tenant_type) : 'Platform'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader title="Account Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status</span>
                {currentStatus === null ? (
                  /* No `status` in the payload: show what the server did say
                     (`is_active`) rather than guessing which of the two
                     not-active states this is. */
                  <Badge variant={user.is_active ? 'success' : 'default'}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                ) : (
                  <Badge variant={USER_STATUS_BADGE_VARIANT[currentStatus]}>
                    {user.status_display || USER_STATUS_LABELS[currentStatus]}
                  </Badge>
                )}
              </div>
              {currentStatus !== null && currentStatus !== 'active' && (
                <p className="text-sm text-gray-500">
                  This account cannot log in. The login form tells them it is{' '}
                  {USER_STATUS_LABELS[currentStatus].toLowerCase()}.
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Verified</span>
                <Badge variant={user.is_verified ? 'success' : 'warning'}>
                  {user.is_verified ? 'Verified' : 'Unverified'}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader title="Activity" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CalendarIcon className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDateTime(user.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Login</p>
                  <p className="text-sm font-medium text-gray-900">
                    {user.last_login ? formatDateTime(user.last_login) : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <UserAccountActions userId={id!} userName={user.full_name} currentStatus={currentStatus} />
          </Card>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={deleteMutation.isPending}
          >
            Delete User
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
